import jwtDecode from "jwt-decode";
import dayjs from "dayjs";

import currentUser from "./CESUser";
import CESAppInfo from "./CESAppInfo";
import * as account from "./CESAccount";
import * as cesRequest from "./CESRequest";
import { endOverlay, insertOverlay, startOverlay, customSetTimeout } from "./CESUtils";

/** AWS-specific parameters that are no longer accepted. */
const DEPRECATED_AWS_PARAMS = [
  "region",
  "awsRegion",
  "userPoolId",
  "identityPoolId",
  "apiGateway",
  "apiGatewayUrl",
  "apiKey",
  "awsAccessKeyId",
  "awsSecretAccessKey",
  "stackPrefix",
  "stage",
] as const;

export interface CESInitConfig {
  /** Required. Base URL of the GCP Cloud Run backend (e.g. https://emea.gcds.coke.com). */
  apiBaseUrl: string;
  /** Required. Base URL of the GCS CDN (e.g. https://cdn.emea.gcds.coke.com). */
  cdnBaseUrl: string;
  /** Required. Client identifier. */
  clientId: string;
  /** Required. Basic credentials payload (base64 of user:pass), with or without the Basic prefix. */
  basicAuth: string;
  /** BCP-47 locale string (e.g. en-US). */
  locale?: string;
  /** reCAPTCHA site key. */
  recaptchaKey?: string;
  /** Legacy alias for recaptchaKey — still accepted. */
  reCaptchakey?: string;
  /** Site / experience name. */
  siteName?: string;
  /** Bundle resource path. */
  bundlePath?: string;
  /** Social sign-in settings. */
  socialSettings?: any;
  /** Any additional non-AWS options are passed through. */
  [key: string]: any;
}

/**
 * Initializes the CES SDK with GCP-based configuration.
 *
 * @param cesConfig - SDK configuration object.
 * @returns A promise that resolves when the SDK is ready.
 */
export const init = async (cesConfig: CESInitConfig): Promise<void> => {
  // Emit deprecation warnings for any AWS-specific params that were passed.
  for (const param of DEPRECATED_AWS_PARAMS) {
    if (Object.prototype.hasOwnProperty.call(cesConfig, param)) {
      console.warn(`[cds] '${param}' is no longer supported and will be ignored.`);
    }
  }

  // Validate required GCP parameters.
  if (!cesConfig.apiBaseUrl) {
    return Promise.reject(new Error("[cds] apiBaseUrl is required"));
  }
  if (!cesConfig.cdnBaseUrl) {
    return Promise.reject(new Error("[cds] cdnBaseUrl is required"));
  }
  if (!cesConfig.clientId) {
    return Promise.reject(new Error("[cds] clientId is required"));
  }
  if (!cesConfig.basicAuth || !cesConfig.basicAuth.trim()) {
    return Promise.reject(new Error("[cds] basicAuth is required"));
  }

  const normalizedBasicAuth = cesConfig.basicAuth.replace(/^Basic\s+/i, "").trim();
  if (!normalizedBasicAuth) {
    return Promise.reject(new Error("[cds] basicAuth is required"));
  }

  // Map public API fields to internal CESAppInfo properties.
  CESAppInfo.ciamClientId = cesConfig.clientId;
  CESAppInfo.locale = cesConfig.locale ?? "en-US";
  CESAppInfo.reCaptchakey = cesConfig.recaptchaKey ?? cesConfig.reCaptchakey;
  CESAppInfo.experienceName = cesConfig.siteName;
  CESAppInfo.basicAuth = normalizedBasicAuth;

  CESAppInfo.apiBaseUrl = cesConfig.apiBaseUrl.replace(/\/+$/, "");
  let parsedUrl: URL;
  try {
    // Must be absolute — host is required below for cross-origin flows
    // (e.g. social login popups), and the sample site's own origin is not
    // reliably the API's origin (they're commonly served from different
    // domains/load balancers).
    parsedUrl = new URL(CESAppInfo.apiBaseUrl);
  } catch {
    return Promise.reject(
      new Error(`[cds] apiBaseUrl must be an absolute URL, got: "${CESAppInfo.apiBaseUrl}"`)
    );
  }
  CESAppInfo.host = parsedUrl.host;
  // endpoint is kept empty — apiUrl() uses apiBaseUrl as the full base,
  // so CESConfiguration routes must be relative suffixes only (e.g. /consumer/forms)
  CESAppInfo.endpoint = "";

  CESAppInfo.cdnBaseUrl = cesConfig.cdnBaseUrl.replace(/\/+$/, "");

  CESAppInfo.bundlePath = cesConfig.bundlePath;
  CESAppInfo.socialSettings = cesConfig.socialSettings;

  await insertOverlay();
  await cesRequest.getBundle();
}

/**
 * Gets user info and evetually restores session
 * @returns a promise returning the user info
 */
export const getUserInfo = async () => {
  const response = await account.getUserInfo();
  await isSessionActive();
  return response.data;
}

/**
 * Checks local storage to see if the user has an active session
 * @returns Resolved or rejected promise
 */
export const isSessionActive = () => {

  if (!currentUser.getUserId()) {
    const accessToken = currentUser.getAccessToken();
    if (accessToken) {
      const tokenClaims: any = jwtDecode(accessToken);
      currentUser.setEmail(tokenClaims.emailAddress);
      currentUser.setUserId(tokenClaims.uuid);
    }
  }

  const refreshToken = currentUser.getRefreshToken();
  if (refreshToken) {
    const tokenClaims: any = jwtDecode(refreshToken);
    const remaining = tokenClaims.exp - dayjs().unix();
    
    if (remaining > 0) {
      if (!(window as any).unload) {
        const timeout = customSetTimeout(() => {
          currentUser.clear();
          window.location.replace("/");
        }, remaining * 1000);

        (window as any).unload = () => {clearTimeout(timeout);}
      }

      return Promise.resolve();
    }

    currentUser.clear();
    window.location.replace("/");
    return Promise.resolve();
  }

  return Promise.reject();
}

export const editProfile = async (formWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  const response = await account.editProfile(formWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback);
  await isSessionActive();
  return response;
}

export const register = async (formWrapperId: string, postLoginWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  const response = await account.register(formWrapperId, postLoginWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback);
  return response;
}

export const signIn = async (formWrapperId: string, postLoginWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  try {
    const response = await account.signIn(formWrapperId, postLoginWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback);
    return response;
  } finally {
    endOverlay();
  }
}

export const forgotPassword = async (formWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  const response = await account.forgotPassword(formWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback);
  return response;
}

export const resetPassword = async (formWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  const response = await account.resetPassword(formWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback);
  return response;
}

export const socialSignIn = async (
  formWrapperId: string,
  mergeAccountWrapperId: string,
  postLoginWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  registerHrefTarget?: string,
  isNewEndpoint?: boolean,
  endpointResponse?: any,
) => {
  try {
    await startOverlay(overlayConfig);
    await account.socialSignIn(formWrapperId, mergeAccountWrapperId, postLoginWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback, registerHrefTarget, isNewEndpoint, endpointResponse);
  } finally {
    endOverlay();
  }
}

/**
 * Sends an activity indicating that the user logged out.
 */
export const logout = async (hrefTarget?: string) => {
  if (!currentUser.getUserId()) return;
  await account.logout(hrefTarget);
}

/**
 * Change password
 * @param formWrapperId
 * @param hrefTarget
 */
export const changePassword = async (formWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  const response = await account.changePassword(formWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback)
  await isSessionActive();
  return response;
}

export const resendEmailVerification = async (formWrapperId: string, overlayConfig?: any, hrefTarget?: string, successCallback?: Function, failureCallback?: Function) => {
  const response = await account.resendEmailVerification(formWrapperId, overlayConfig, hrefTarget, successCallback, failureCallback);
  return response;
}

/**
 * Email Verification Proxy
 * @param {string} wrapperId
 * @param {any?} overlayConfig Optional
 * @param {string?} hrefTarget - location
 * @param {string?} postLoginWrapperId - the post login wrapper element ID. Required only if direct login is needed
 * @param {Function?} successCallback - Function to be executed after successful form submission Optional
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise}
 */
export const emailVerification = async (
  wrapperId: string, 
  overlayConfig?: any, 
  hrefTarget?: string,
  postLoginWrapperId?: string,
  successCallback?: Function,
  failureCallback?: Function,
) => {
  let response;

  if (postLoginWrapperId) {
    response = await account.emailVerificationWithDirectLogin(
      wrapperId,
      postLoginWrapperId,
      overlayConfig,
      hrefTarget,
      successCallback,
      failureCallback
    );
  } else {
    response = await account.emailVerification(
      wrapperId,
      overlayConfig, 
      hrefTarget,
    );
  }
  return response;
}

/**
 *
 * @param wrapperId
 * @param validateWrapperId
 * @param overlayConfig
 * @param registerHrefTarget
 * @param experienceHrefTarget
 * @returns
 */
export const mobileSignIn = async (
  wrapperId: string,
  validateWrapperId: string,
  mergeAccountWrapperId: string,
  overlayConfig?: any,
  registerHrefTarget?: string,
  experienceHrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  const response = await account.mobileSignIn(wrapperId, validateWrapperId, mergeAccountWrapperId, overlayConfig, registerHrefTarget, experienceHrefTarget, successCallback, failureCallback);
  return response;
}

export { endOverlay, getVersion, startOverlay } from "./CESUtils";
export { default as currentUser } from "./CESUser";
